import { SolText } from '@sol/components';
import { ObjectTools } from '@sol/components/tools/object-tools';
import { SolIcon } from '@sol/icons';
import type { LabIconMood } from '../lab-icon/lab-icon.component';
import { LabAction, LabActionMood, LabActionTools } from '../../tools/lab-action';

export type LabNotificationMood = 'default' | 'info' | 'success' | 'warning' | 'danger';

export class LabNotification {
  public readonly hostClass: string;
  public readonly icon: SolIcon | undefined;
  public readonly iconMood: LabIconMood | undefined;
  public readonly actionButtonMood: LabActionMood;
  public readonly hasIcon: boolean;
  public readonly hasHeading: boolean;
  public readonly hasDescription: boolean;
  public readonly hasContent: boolean;
  public readonly hasAction: boolean;
  public readonly hasControls: boolean;

  private constructor(
    public readonly heading: SolText,
    public readonly description: SolText | undefined,
    public readonly mood: LabNotificationMood,
    public readonly action: LabAction | undefined,
    public readonly secondaryAction: LabAction | undefined,
    public readonly dismissible: boolean,
    public readonly timed: boolean,
    hostClass: string,
    icon: SolIcon | undefined,
    iconMood: LabIconMood | undefined,
    actionButtonMood: LabActionMood,
    hasIcon: boolean,
    hasHeading: boolean,
    hasDescription: boolean,
    hasContent: boolean,
    hasAction: boolean,
    hasControls: boolean,
  ) {
    this.hostClass = hostClass;
    this.icon = icon;
    this.iconMood = iconMood;
    this.actionButtonMood = actionButtonMood;
    this.hasIcon = hasIcon;
    this.hasHeading = hasHeading;
    this.hasDescription = hasDescription;
    this.hasContent = hasContent;
    this.hasAction = hasAction;
    this.hasControls = hasControls;
  }

  public static of(): LabNotification {
    return new LabNotification(
      SolText.ofRaw('Notification'),
      undefined,
      'default',
      undefined,
      undefined,
      false,
      true,
      '',
      undefined,
      undefined,
      'primary',
      false,
      true,
      false,
      true,
      false,
      false,
    ).recalculate();
  }

  public withHeading(heading: SolText): LabNotification {
    return this.copy({ heading }).recalculate();
  }

  public withDescription(description: SolText | undefined): LabNotification {
    return this.copy({ description }).recalculate();
  }

  public withMood(mood: LabNotificationMood): LabNotification {
    return this.copy({ mood }).recalculate();
  }

  public withAction(action: LabAction | undefined): LabNotification {
    return this.copy({ action }).recalculate();
  }

  public withSecondaryAction(secondaryAction: LabAction | undefined): LabNotification {
    return this.copy({ secondaryAction }).recalculate();
  }

  public withDismissible(dismissible: boolean): LabNotification {
    return this.copy({ dismissible }).recalculate();
  }

  private recalculate(): LabNotification {
    // `dismissible` is the public API. Non-dismissible toasts are timed by
    // definition, so consumers never have to sync two lifecycle props.
    const timed = !this.dismissible;
    const icon = LabNotification.toIcon(this.mood);
    const hasHeading = !this.heading.isEmpty();
    const hasDescription = this.description !== undefined && !this.description.isEmpty();
    const hasAction = LabActionTools.isVisible(this.action);
    const hasSecondaryAction = LabActionTools.isVisible(this.secondaryAction);
    return this.copy({
      hostClass: `lab-notification lab-notification--${this.mood}`,
      icon,
      iconMood: LabNotification.toIconMood(this.mood),
      actionButtonMood: LabNotification.toActionButtonMood(this.mood),
      timed,
      hasIcon: icon !== undefined,
      hasHeading,
      hasDescription,
      hasContent: hasHeading || hasDescription,
      hasAction: hasAction || hasSecondaryAction,
      hasControls: hasAction || hasSecondaryAction || this.dismissible,
    });
  }

  private static toIcon(mood: LabNotificationMood): SolIcon | undefined {
    switch (mood) {
      case 'success':
        return SolIcon.check;
      case 'warning':
        return SolIcon.warning;
      case 'danger':
        return SolIcon.exclamation;
      case 'info':
        return SolIcon.info;
      case 'default':
        return undefined;
    }
  }

  private static toIconMood(mood: LabNotificationMood): LabIconMood | undefined {
    switch (mood) {
      case 'success':
      case 'warning':
      case 'danger':
      case 'info':
        return mood;
      case 'default':
        return undefined;
    }
  }

  private static toActionButtonMood(mood: LabNotificationMood): LabActionMood {
    // The primary toast action inherits notification intent. Neutral toasts use
    // the regular primary action color so the action still reads as the next step.
    switch (mood) {
      case 'success':
      case 'warning':
      case 'danger':
      case 'info':
        return mood;
      case 'default':
        return 'primary';
    }
  }

  private copy(props: Partial<LabNotification>): LabNotification {
    return ObjectTools.copy<LabNotification>(this, props);
  }
}
